import { sanitizeLocale } from "lib/safeDisplayText";

/**
 * Translations and labels for the DGA second navigation bar (utility strip).
 * - UI strings: English (default) and Arabic.
 * - Weather: Open-Meteo WMO codes → short words (same languages).
 */

export type SecondNavMessages = {
  unavailable: string;
  a11y: {
    section: string;
    contextList: string;
    toolbar: string;
    weather: string;
    date: string;
    time: string;
    location: string;
    zoomOut: string;
    zoomIn: string;
    contrastOn: string;
    contrastOff: string;
    /** Mic action (Figma second nav); reserved until speech API */
    voiceInput: string;
    /** Grayscale / contrast (Figma “view” control) */
    viewContrast: string;
    /** While geolocation is resolving or weather/location APIs are loading */
    loadingWeather: string;
    loadingLocation: string;
  };
  zoomWithPercent: (pct: number) => string;
};

const EN: SecondNavMessages = {
  unavailable: "—",
  a11y: {
    section: "Site utilities and accessibility",
    contextList: "Local date, time, weather, and location",
    toolbar: "Page display and contrast",
    weather: "Weather",
    date: "Date",
    time: "Time",
    location: "Location",
    zoomOut: "Decrease text size",
    zoomIn: "Increase text size",
    contrastOn: "Disable grayscale contrast mode",
    contrastOff: "Enable grayscale contrast mode",
    voiceInput: "Voice input",
    viewContrast: "Contrast and visibility",
    loadingWeather: "Loading weather",
    loadingLocation: "Loading location",
  },
  zoomWithPercent: (pct) => `Current size ${pct} percent`,
};

const AR: SecondNavMessages = {
  unavailable: "—",
  a11y: {
    section: "خدمات الموقع وإمكانية الوصول",
    contextList: "التاريخ والوقت والطقس والموقع المحلي",
    toolbar: "عرض الصفحة والتباين",
    weather: "الطقس",
    date: "التاريخ",
    time: "الوقت",
    location: "الموقع",
    zoomOut: "تصغير حجم النص",
    zoomIn: "تكبير حجم النص",
    contrastOn: "إيقاف وضع التباين الرمادي",
    contrastOff: "تشغيل وضع التباين الرمادي",
    voiceInput: "إدخال صوتي",
    viewContrast: "التباين والرؤية",
    loadingWeather: "جاري تحميل الطقس",
    loadingLocation: "جاري تحميل الموقع",
  },
  zoomWithPercent: (pct) => `الحجم الحالي ${pct}٪`,
};

/** First segment of a locale tag, e.g. "ar-SA" → "ar". */
function primaryLanguage(locale: string): string {
  return sanitizeLocale(locale).split("-")[0]?.toLowerCase() ?? "en";
}

/** BigDataCloud wants a two-letter ISO 639-1 code for place names language. */
export function getGeocodeLanguageCode(locale: string): string {
  const lang = primaryLanguage(locale);
  return /^[a-z]{2}$/.test(lang) ? lang : "en";
}

export function getSecondNavMessages(locale: string): SecondNavMessages {
  return primaryLanguage(locale) === "ar" ? AR : EN;
}

type WeatherWord = "clear" | "cloudy" | "fog" | "rain" | "snow" | "showers" | "storm";

const WEATHER_EN: Record<WeatherWord, string> = {
  clear: "Clear",
  cloudy: "Cloudy",
  fog: "Fog",
  rain: "Rain",
  snow: "Snow",
  showers: "Showers",
  storm: "Storm",
};

const WEATHER_AR: Record<WeatherWord, string> = {
  clear: "صافٍ",
  cloudy: "غائم",
  fog: "ضباب",
  rain: "مطر",
  snow: "ثلج",
  showers: "زخات مطر",
  storm: "عاصفة",
};

/**
 * Maps Open-Meteo / WMO weather codes to a short label.
 * Ranges follow common WMO groupings (clear, clouds, fog, rain, snow, showers, thunderstorm).
 */
export function weatherCodeToLabel(code: number, locale: string): string {
  const words = primaryLanguage(locale) === "ar" ? WEATHER_AR : WEATHER_EN;

  let key: WeatherWord = "cloudy";
  if (code === 0) key = "clear";
  else if (code >= 1 && code <= 3) key = "cloudy";
  else if (code >= 45 && code <= 48) key = "fog";
  else if (code >= 51 && code <= 67) key = "rain";
  else if (code >= 71 && code <= 77) key = "snow";
  else if (code >= 80 && code <= 82) key = "showers";
  else if (code >= 95) key = "storm";

  return words[key];
}
